import { memo, useState } from 'react';
import { useAsync } from 'react-use';
import cn from 'classnames';
import { IPlayer, MATCH_EVENT } from '@zmedia/bm-soccer-ui/interface';
import {
    ArrayUtils,
    ColorUtils,
    ObjectUtils,
} from '@zmedia/bm-soccer-ui/utils';
import MatchService from '@zmedia/bm-soccer-ui/service/MatchService';
import {
    Element,
    Icon,
    Image,
    RouterLink,
    Section,
    Title,
} from '@zmedia/bm-soccer-ui/components';
import { URL_SOCCER_PAGE } from '@zmedia/bm-soccer-ui/modules/shared';
import { AWAY_POSITION, HOME_POSITION } from './constant';
import { PlayerInLineup } from './player';
import { Coach } from '../coach';
import { BenchesList, SubstitutionList, OfficialList } from './list';
import styles from './style.module.scss';

const filterSubstitution = (players: IPlayer[] = []) => {
    const substitutionPlayer = players.filter((player: IPlayer) => {
        if (!player.events?.length) {
            return null;
        }
        return player.events.some(
            (event) => event.type.id === MATCH_EVENT.SUBSTITUTION
        );
    });
    return substitutionPlayer;
};

export function MatchLineup(props: any) {
    let data = props.lineup || {};
    if (ObjectUtils.isEmpty(data)) {
        const state = useAsync(() => MatchService.getLineup(props.id));
        data = state.value?.data || {};
    }
    const [expand, setIsExpand] = useState<boolean>(false);

    const renderPlayers = (
        items: IPlayer[] = [],
        tactic: number = 0,
        team: string
    ) => {
        if (!tactic) {
            return null;
        }
        return items.map((item, index) => {
            const style =
                team === 'home'
                    ? HOME_POSITION[tactic][index + 1]
                    : AWAY_POSITION[tactic][index + 1];

            return <PlayerInLineup item={item} key={index} style={style} />;
        });
    };

    if (ObjectUtils.isEmpty(data)) {
        return null;
    }

    const subsHomePlayer = filterSubstitution(data.home.subPlayers);
    const subsAwayPlayer = filterSubstitution(data.away.subPlayers);

    return (
        <Section className={styles['match-detail-section']} name="match-lineup">
            <Element className={styles['lineup-title-container']}>
                <RouterLink
                    className={cn(styles['lineup-item'], styles['home'])}
                    to={`${URL_SOCCER_PAGE.SUBPATH}${data.home.team.path}`}
                    title={data.home.team.name}
                >
                    <Image
                        src={data.home.team.logo}
                        alt={data.home.team.name}
                    />
                    {data?.home?.formation ? (
                        <Element
                            className={styles['lineup-text']}
                            style={{
                                color: ColorUtils.isBrightnessColor(
                                    props?.teamColor?.hColor
                                )
                                    ? ''
                                    : '#fff',
                            }}
                        >
                            {data.home.formation}
                        </Element>
                    ) : (
                        <Element
                            className={styles['lineup-text']}
                            style={{
                                color: ColorUtils.isBrightnessColor(
                                    props?.teamColor?.aColor
                                )
                                    ? ''
                                    : '#fff',
                            }}
                        >
                            {data.home.team.name}
                        </Element>
                    )}
                </RouterLink>

                <RouterLink
                    className={cn(styles['lineup-item'], styles['away'])}
                    to={`${URL_SOCCER_PAGE.SUBPATH}${data.away.team.path}`}
                    title={data.away.team.name}
                >
                    {data?.away?.formation ? (
                        <Element
                            className={styles['lineup-text']}
                            style={{
                                color: ColorUtils.isBrightnessColor(
                                    props?.teamColor?.aColor
                                )
                                    ? ''
                                    : '#fff',
                            }}
                        >
                            {data.away.formation}
                        </Element>
                    ) : (
                        <Element
                            className={styles['lineup-text']}
                            style={{
                                color: ColorUtils.isBrightnessColor(
                                    props?.teamColor?.aColor
                                )
                                    ? ''
                                    : '#fff',
                            }}
                        >
                            {data.away.team.name}
                        </Element>
                    )}
                    <Image
                        src={data.away.team.logo}
                        alt={data.away.team.name}
                    />
                </RouterLink>
            </Element>

            {ObjectUtils.isEmpty(data.home.formation) &&
            ObjectUtils.isEmpty(data.away.formation) ? (
                <Element className={styles['benches-container']}>
                    <Title renderAs="button">Đội hình chính thức</Title>
                    <Element className={styles['benches-list']}>
                        <OfficialList players={data.home.players} />
                        <OfficialList players={data.away.players} />
                    </Element>
                </Element>
            ) : (
                <>
                    <Element className={styles['lineup-map-container']}>
                        {data.home.formation && (
                            <Element className={cn(styles['team-container'])}>
                                {renderPlayers(
                                    data.home.players,
                                    data.home.tactic,
                                    'home'
                                )}
                            </Element>
                        )}
                        {data.away.formation && (
                            <Element className={cn(styles['team-container'])}>
                                {renderPlayers(
                                    data.away.players,
                                    data.away.tactic,
                                    'away'
                                )}
                            </Element>
                        )}
                    </Element>
                    <Coach home={data.home.coach} away={data.away.coach} />
                </>
            )}

            {ArrayUtils.isEmpty(subsHomePlayer) &&
            ArrayUtils.isEmpty(subsAwayPlayer) ? (
                <></>
            ) : (
                <Element className={styles['benches-container']}>
                    <Title>Thay Người</Title>
                    <Element className={styles['benches-list']}>
                        <SubstitutionList players={subsHomePlayer} />
                        <SubstitutionList players={subsAwayPlayer} />
                    </Element>
                </Element>
            )}

            {ObjectUtils.isEmpty(data?.home?.subPlayers) &&
            ObjectUtils.isEmpty(data?.away?.subPlayers) ? (
                <></>
            ) : (
                <Element className={styles['benches-container']}>
                    <Title
                        renderAs="button"
                        onClick={() => setIsExpand(!expand)}
                    >
                        Đội hình dự bị <Icon icon={expand ? 'up' : 'down'} />
                    </Title>
                    <Element
                        className={cn(styles['benches-list'], {
                            [styles['is-shrink']]: !expand,
                        })}
                    >
                        <BenchesList players={data.home.subPlayers} />
                        <BenchesList players={data.away.subPlayers} />
                    </Element>
                </Element>
            )}
        </Section>
    );
}

export default memo(MatchLineup);
